import React, { createContext, useContext, useEffect, useState } from 'react';
import { PALETTES, DEFAULT_PALETTE, applyPalette } from '../lib/themes';

const ThemeContext = createContext({
  theme: 'light',
  setTheme: () => {},
  toggle: () => {},
  palette: DEFAULT_PALETTE,
  setPalette: () => {},
  companyPalette: null,
});

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => {
    try { return localStorage.getItem('stockaris_theme') || 'light'; } catch { return 'light'; }
  });

  // User-level explicit choice (or null = "use company default")
  const [palette, setPaletteState] = useState(() => {
    try {
      const saved = localStorage.getItem('stockaris_palette');
      return saved && PALETTES[saved] ? saved : null;
    } catch { return null; }
  });

  // Company-level default fetched from /api/companies/me at boot (after auth)
  const [companyPalette, setCompanyPalette] = useState(null);

  // Resolve effective palette: user override > company default > DEFAULT
  const effectivePalette = palette || companyPalette || DEFAULT_PALETTE;

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') root.classList.add('dark');
    else root.classList.remove('dark');
    try { localStorage.setItem('stockaris_theme', theme); } catch { /* ignore */ }
    applyPalette(effectivePalette, theme);
  }, [theme, effectivePalette]);

  // Lazy-fetch the company branding once on mount (best-effort; ignore 401)
  useEffect(() => {
    let cancelled = false;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/companies/me`;
    fetch(url, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled) return;
        const cp = data?.branding?.palette;
        if (cp && PALETTES[cp]) setCompanyPalette(cp);
      })
      .catch(() => { /* not authenticated yet — fine */ });
    return () => { cancelled = true; };
  }, []);

  const setTheme = (t) => setThemeState(t);
  const toggle = () => setThemeState((t) => (t === 'dark' ? 'light' : 'dark'));
  const setPalette = (p) => {
    if (!PALETTES[p]) return;
    try { localStorage.setItem('stockaris_palette', p); } catch { /* ignore */ }
    setPaletteState(p);
  };
  const clearUserPalette = () => {
    try { localStorage.removeItem('stockaris_palette'); } catch { /* ignore */ }
    setPaletteState(null);
  };

  return (
    <ThemeContext.Provider value={{
      theme, setTheme, toggle,
      palette: effectivePalette,
      userPalette: palette,
      companyPalette,
      setPalette,
      clearUserPalette,
      setCompanyPalette,
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
